import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Step,
  StepButton,
  StepConnector,
  StepLabel,
  Stepper,
  stepConnectorClasses,
  stepLabelClasses,
} from "@mui/material";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CAMPAIGN_BUILDER_STEPS,
  CAMPAIGN_NAME_TEXT,
  type CampaignBuilderStepNumber,
  FIRST_STEP,
  LAST_STEP,
  STEP_LOCKED_LABEL,
} from "@/constants/campaignBuilder";
import { useCampaignBuilder, type ValidationIssue } from "@/context/CampaignBuilderContext";
import { getCurrentStep, getStepPath } from "@/lib/campaigns";
import type { ConfigTab } from "@/types/types";
import { isBudgetLocked, isRecipientsLocked } from "@/utils/campaign-gating-helper";
import { isSelfSendMode } from "@/utils/campaignMode-helper";
import { hasLandingVideo } from "@/utils/landing-video-helper";

function GfgStepIcon(props: { active?: boolean; completed?: boolean; icon: ReactNode }) {
  const { active = false, completed = false, icon } = props;
  const filled = active || completed;
  return (
    // A span, not a div: on visited steps this icon renders inside the
    // StepButton, and a button may only contain phrasing content.
    <span
      className={`w-6 h-6 min-w-6 rounded-full flex items-center justify-center font-lato text-base leading-none ${
        filled ? "bg-dark-dust text-white" : "bg-smoke text-graphite"
      }`}
    >
      {icon}
    </span>
  );
}

/**
 * Clickable steps — visited ones, and forward ones whose prerequisites are met —
 * are rendered as a StepButton (a real `<button>`), so they are keyboard
 * reachable. MUI's default 24px/16px padding with matching negative margins is
 * zeroed out: it would leave the focus ring floating far outside the strip, and
 * without it the clickable step occupies exactly the same box as a plain
 * StepLabel, so the stepper's spacing is unchanged.
 *
 * The underline is permanent on clickable steps, so the link is discoverable without
 * hovering. That is a deliberate divergence: the prototype underlines only on hover
 * (`.step.clickable:hover .step-label`, Stage2-Revisions.html:102).
 */
const stepButtonSx = {
  p: 0,
  m: 0,
  width: "auto",
  borderRadius: "4px",
  // Scoped to the label slot, never the StepLabel root: `text-decoration` propagates
  // to in-flow descendants, so underlining the root drags the numeral inside the step
  // icon along with it.
  [`& .${stepLabelClasses.label}`]: {
    textDecoration: "underline",
  },
  "&:focus-visible": {
    outline: "2px solid var(--color-dark-dust)",
    outlineOffset: "2px",
  },
};

export default function CampaignBuilderNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentStep = getCurrentStep(pathname);
  const isFirstStep = currentStep === FIRST_STEP;
  const isLastStep = currentStep === LAST_STEP;

  const {
    step1Complete,
    step2Complete,
    step3Complete,
    notificationMode,
    stage3Unboxing,
    stage3Email,
    stage3Landing,
    setDesignActiveTab,
    setDesignDrawerOpen,
    setEmailConfigTab,
    setLandingConfigTab,
    showValidation,
    clearValidation,
    saveStepIfDirty,
    saveStep1OnLeave,
    setPendingFeed,
    isStepDirty,
    openModal,
    closeModal,
    campaignStatus,
    campaignName,
    openCampaignRename,
  } = useCampaignBuilder();

  // Step 2 gates only in manual mode (needs a sender first name + company); gfg is always complete.
  const canContinue =
    !isLastStep && (currentStep !== 1 || step1Complete) && (currentStep !== 2 || step2Complete);

  /**
   * Navigate away from the current step, saving any un-persisted change first. This covers ALL
   * exits — Continue, Previous, step-chip jumps (either direction), and the dashboard button — so
   * changes are never lost no matter how the user leaves. Steps 2/4 flush silently; Step 1 flushes
   * silently too, except an unsaved Edit Collection grid is AWAITED — if that save fails, or the
   * grid changed while it was in flight, the user stays and the same modal offers retry (primary),
   * discard (secondary) or stay (dismiss). Step 3 is autosave-based, so if it changed since the
   * last autosave it WARNS first via the shared unsaved-changes modal — Save & leave (primary),
   * leave without saving (secondary), or cancel (dismiss = stay).
   */
  function navigateLeaving(target: string) {
    if (currentStep === 3 && isStepDirty(3)) {
      openModal({
        context: "unsavedChanges",
        onPrimary: () => {
          void saveStepIfDirty(3);
          navigate(target);
        },
        onSecondary: () => navigate(target),
      });
      return;
    }
    if (currentStep === 1) {
      void saveStep1OnLeave().then((saved) => {
        if (saved) {
          // A Step 1 modal opened during the save must not survive onto the next page.
          closeModal();
          navigate(target);
          return;
        }
        // Failed (its toast already showed) or the grid changed mid-save: a pending grid is still
        // held, so stay and offer to save it.
        openModal({
          context: "unsavedChanges",
          onPrimary: () => navigateLeaving(target),
          onSecondary: () => {
            setPendingFeed(null);
            void saveStepIfDirty(1);
            navigate(target);
          },
        });
      });
      return;
    }
    void saveStepIfDirty(currentStep);
    navigate(target);
  }

  function handlePrevious() {
    clearValidation();
    navigateLeaving(getStepPath((currentStep - 1) as CampaignBuilderStepNumber));
  }

  /** Jump to a design canvas tab with its customize drawer open on the given sub-tab —
   *  the "Fix" target for step-3 validation issues. */
  function openEmailDrawer(tab: ConfigTab) {
    setDesignActiveTab("email");
    setEmailConfigTab(tab);
    setDesignDrawerOpen(true);
  }
  function openLandingDrawer(tab: ConfigTab) {
    setDesignActiveTab("landing");
    setLandingConfigTab(tab);
    setDesignDrawerOpen(true);
  }

  /** The specific unmet step-3 requirements, each with its fix — the detail behind
   *  `step3Complete`, rendered in the validation toast. Keep in sync with step3Complete. */
  function collectStep3Issues(): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    if (stage3Unboxing.showGiftMessage && !stage3Unboxing.giftMessageText.trim()) {
      issues.push({
        tab: "Gift Message",
        message: "Enter a gift message or turn off the Gift Message toggle.",
        onFix: () => openLandingDrawer("message"),
      });
    }
    // The Video field lives on the landing "message" sub-tab, so Fix routes there.
    if (stage3Landing.showVideo && !hasLandingVideo(stage3Landing.videoUrl)) {
      issues.push({
        tab: "Landing Video",
        message: "Add a video link or turn off the Video toggle.",
        onFix: () => openLandingDrawer("message"),
      });
    }
    // Self-send (manual + universal) locks the Email tab, so its logo requirement doesn't apply.
    if (
      !isSelfSendMode(notificationMode) &&
      stage3Email.logoEnabled &&
      !stage3Email.logoUrl.trim()
    ) {
      issues.push({
        tab: "Email Logo",
        message: "Upload a logo or turn off the Company Logo toggle.",
        onFix: () => openEmailDrawer("branding"),
      });
    }
    // A synced landing logo mirrors the email logo, not the landing's own `logoUrl` — resolve it
    // the same way step3Complete does so a synced landing isn't wrongly flagged as missing.
    const landingLogoUrl = stage3Landing.logoSynced ? stage3Email.logoUrl : stage3Landing.logoUrl;
    if (stage3Landing.logoEnabled && !landingLogoUrl.trim()) {
      issues.push({
        tab: "Landing Logo",
        message: "Upload a logo or turn off the Company Logo toggle.",
        onFix: () => openLandingDrawer("branding"),
      });
    }
    return issues;
  }

  function handleContinue() {
    if (!canContinue) return;

    // Step 3 (Design): block advancing while incomplete and surface the fixes in the toast.
    if (currentStep === 3 && !step3Complete) {
      showValidation(collectStep3Issues());
      return;
    }

    clearValidation();
    // Advancing flushes the step we're leaving if it changed (Step 3 warns first). Fire-and-
    // forget for 2/4 and Step 1's meta flush (a failure surfaces its own toast); a pending Step 1
    // grid is awaited and blocks leaving on failure.
    navigateLeaving(getStepPath((currentStep + 1) as CampaignBuilderStepNumber));
  }

  /**
   * Per-step "conditions met" — the same gates Continue enforces when leaving each step. Step 2
   * blocks only a manual campaign missing its sender details (gfg is always met); step 4 is the
   * last step, so it is never a prerequisite for a jump.
   */
  const stepConditionsMet: Record<CampaignBuilderStepNumber, boolean> = {
    1: step1Complete,
    2: step2Complete,
    3: step3Complete,
    4: true,
  };

  /**
   * Per-step "open but read-only" — the padlock the strip shows. Deliberately NOT a navigation
   * gate: a locked step still opens (in its locked state), so the sender can read what was
   * scheduled. Steps 3 and 4 stay editable at every editable status, hence hard `false`.
   * `campaignStatus === null` (CREATE mode) locks nothing — see campaign-gating-helper.
   */
  const stepLocked: Record<CampaignBuilderStepNumber, boolean> = {
    1: isBudgetLocked(campaignStatus),
    2: isRecipientsLocked(campaignStatus),
    3: false,
    4: false,
  };

  /** A forward target is reachable when every step between here and it (the target
   *  excluded) already meets its conditions — the reviewer-requested forward click
   *  (review, 2026-08-17), which diverges from the prototype's back-only goToStage()
   *  (`if (targetStage >= currentStage) return;`, Stage2-Revisions.html:11917). */
  const isForwardReachable = (target: CampaignBuilderStepNumber) =>
    CAMPAIGN_BUILDER_STEPS.filter((s) => s.number >= currentStep && s.number < target).every(
      (s) => stepConditionsMet[s.number],
    );

  /**
   * Only reachable steps are rendered as buttons, so the guard is structural here —
   * the current step, and any step ahead whose prerequisites are unmet, are inert
   * text; incomplete forward moves stay behind Continue, which owns the validation.
   *
   * Going BACK does not commit anything (like handlePrevious — nothing is being left
   * behind, the sender is going back to edit). Going FORWARD commits every step it
   * leaves behind, exactly as Continue would one step at a time.
   */
  function handleStepSelect(step: CampaignBuilderStepNumber) {
    if (step > currentStep) clearValidation();
    // Jumping either direction commits the step we're leaving if dirty; a dirty Step 3 warns first.
    navigateLeaving(getStepPath(step));
  }

  return (
    <header
      className="bg-white flex items-center px-5 py-5 w-full shrink-0"
      style={{ borderBottom: "0.5px solid var(--color-smoke)" }}
    >
      <div className="flex items-center gap-11 w-full">
        {/* Dashboard icon + Previous */}
        <div className="flex items-center gap-3 flex-1 basis-0 min-w-0">
          <button
            type="button"
            onClick={() => navigateLeaving("/dashboard")}
            aria-label="Go to dashboard"
            className="size-8.5 border border-dark-dust rounded-btn bg-white flex items-center justify-center hover:bg-light-dust transition-colors shrink-0"
          >
            <DashboardOutlinedIcon sx={{ fontSize: 14, color: "var(--color-dark-dust)" }} />
          </button>
          {!isFirstStep && (
            <button
              type="button"
              onClick={handlePrevious}
              className="h-8.5 border border-dark-dust rounded-btn bg-white flex items-center gap-3 px-5 hover:bg-light-dust transition-colors shrink-0"
            >
              <ArrowBackIcon sx={{ fontSize: 14, color: "var(--color-rodeo-black)" }} />
              <span className="font-lato font-bold text-[11px] tracking-[2px] uppercase text-rodeo-black whitespace-nowrap">
                Previous
              </span>
            </button>
          )}
          {campaignName && (
            <button
              type="button"
              onClick={openCampaignRename}
              title={campaignName}
              aria-label={`${CAMPAIGN_NAME_TEXT.renameAction}: ${campaignName}`}
              className="min-w-0 max-w-[220px] truncate max-[1199px]:hidden font-lato text-[13px] text-graphite bg-transparent border-0 rounded-btn px-2 py-1 cursor-pointer hover:bg-clear-dust hover:text-rodeo-black transition-colors"
            >
              {campaignName}
            </button>
          )}
        </div>

        {/* MUI Stepper — icon+label side by side via flexDirection row override */}
        <Stepper
          activeStep={currentStep - 1}
          connector={
            <StepConnector
              sx={{
                [`&.${stepConnectorClasses.horizontal}`]: { top: 12 },
                [`& .${stepConnectorClasses.line}`]: {
                  borderColor: "var(--color-smoke)",
                  borderTopWidth: 1,
                  marginInline: 2,
                },
              }}
            />
          }
          sx={{
            flex: "2 1 0%",
            p: 0,
            "& .MuiStep-root": { px: 0 },
            [`& .${stepLabelClasses.root}`]: {
              flexDirection: "row",
              alignItems: "center",
              gap: "8px",
              p: 0,
            },
            [`& .${stepLabelClasses.iconContainer}`]: { pr: 0 },
            [`& .${stepLabelClasses.labelContainer}`]: { width: "auto" },
            [`& .${stepLabelClasses.label}`]: {
              fontFamily: "var(--font-lato)",
              fontSize: 16,
              fontWeight: 400,
              mt: 0,
              color: "var(--color-graphite)",
              whiteSpace: "nowrap",
              "&.Mui-active": { color: "var(--color-rodeo-black)", fontWeight: 400 },
              "&.Mui-completed": { color: "var(--color-rodeo-black)", fontWeight: 400 },
            },
          }}
        >
          {CAMPAIGN_BUILDER_STEPS.map((step) => {
            const isCurrent = step.number === currentStep;
            const isVisited = step.number < currentStep;
            const isClickable = !isCurrent && (isVisited || isForwardReachable(step.number));
            const locked = stepLocked[step.number];
            const label = (
              <span className="inline-flex items-center gap-1.5">
                {step.label}
                {locked && (
                  <LockOutlinedIcon
                    sx={{ fontSize: 14, color: "var(--color-graphite)" }}
                    titleAccess={STEP_LOCKED_LABEL}
                  />
                )}
              </span>
            );
            return (
              // aria-current lives on the Step because the current step is not a
              // button; StepButton would have set it itself, but only clickable
              // steps are buttons and those are never the active one.
              //
              // disabled: a linear MUI Stepper auto-disables every step past
              // activeStep, which would leave a reachable forward step as a
              // dead-but-underlined button — overridden where the jump is allowed.
              <Step
                key={step.number}
                aria-current={isCurrent ? "step" : undefined}
                disabled={isClickable ? false : undefined}
              >
                {isClickable ? (
                  <StepButton
                    onClick={() => handleStepSelect(step.number)}
                    disableRipple
                    sx={stepButtonSx}
                    title={locked ? STEP_LOCKED_LABEL : undefined}
                  >
                    <StepLabel slots={{ stepIcon: GfgStepIcon }}>{label}</StepLabel>
                  </StepButton>
                ) : (
                  <StepLabel slots={{ stepIcon: GfgStepIcon }}>{label}</StepLabel>
                )}
              </Step>
            );
          })}
        </Stepper>

        {/* Continue — hidden on the last step, where the Review page owns its own launch
            action. Kept mounted but invisible and inert so the flex-1 Stepper keeps the same
            width and the step labels do not shift when landing on Review. */}
        <div className="flex flex-1 basis-0 min-w-0 justify-end">
          <button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue}
            aria-hidden={isLastStep}
            tabIndex={isLastStep ? -1 : undefined}
            className={`h-8.5 rounded-btn flex items-center gap-3 px-5 border-0 transition-colors shrink-0 ${
              isLastStep
                ? "invisible pointer-events-none"
                : canContinue
                  ? "bg-rodeo-black text-white hover:opacity-90 cursor-pointer"
                  : "bg-smoke text-graphite cursor-not-allowed"
            }`}
          >
            <span className="font-lato font-bold text-[11px] tracking-[2px] uppercase whitespace-nowrap">
              Continue
            </span>
            <ArrowForwardIcon sx={{ fontSize: 14 }} />
          </button>
        </div>
      </div>
    </header>
  );
}
